import { normalizeChartingCollection } from '@/features/charting-next/adapters/chartingCollectionAdapters';
import { chartingMarketQueryRepository } from '@/features/charting-next/api/chartingMarketQueryRepository';
import { chartingQueryKeys, invalidateChartingMarketQuery } from '@/features/charting-next/api/chartingCache';
import {
    CHARTING_MARKET_SELECTIONS,
    buildChartingMarketQueryCacheSignature,
    chartingEntryToMarketQuery,
    chartingSortToMarketSort,
    cloneMarketQuery,
    createEmptyMarketQuery,
    summarizeMarketQuery
} from '@/features/charting-next/models/chartingMarketQuery';
import { MarketExplorerController } from '@/features/stockchart/explorer/marketExplorerController';
import { loadCachedResource } from '@/features/charting-next/services/useRemoteResource';
import { computed, onBeforeUnmount, ref, watch } from '@vue/composition-api';

export function useChartingMarketExplorer({ sessionStore, route, entry, openSource, repository = chartingMarketQueryRepository }) {
    const state = ref(null);
    const activationError = ref(null);
    const activationLoading = ref(false);
    const cachedRepository = {
        ...repository,
        execute: (definition, config = {}) => loadCachedResource({
            key: chartingQueryKeys.marketQuery(sessionStore.uid, buildChartingMarketQueryCacheSignature(definition)),
            loader: () => repository.execute(definition, config),
            ttl: 20000
        })
    };
    const controller = new MarketExplorerController(cachedRepository, (snapshot) => {
        state.value = snapshot;
    });
    let activationVersion = 0;
    let suppressNextFinderActivation = false;

    const routeMapping = computed(() => chartingEntryToMarketQuery(entry.value, route?.query || {}));
    const isActive = computed(() => routeMapping.value.handled);
    const normalizedCollection = computed(() => {
        const collection = normalizeChartingCollection(state.value?.items || [], {
            ...entry.value,
            title: 'Explorar mercado',
            subtitle: summarizeMarketQuery(state.value?.appliedQuery || createEmptyMarketQuery()).label
        });
        const total = state.value?.meta?.total_result_count;

        return {
            ...collection,
            totalItems: Number.isInteger(total) ? total : collection.totalItems,
            meta: state.value?.meta || null,
            emptyMessage: 'No hay valores que cumplan la consulta aplicada.'
        };
    });

    const resource = {
        data: normalizedCollection,
        error: computed(() => activationError.value || state.value?.error || null),
        status: computed(() => activationError.value ? 'error' : activationLoading.value ? 'loading' : state.value?.status || 'idle'),
        isLoading: computed(() => activationLoading.value || state.value?.status === 'loading'),
        isRefreshing: computed(() => false),
        isReady: computed(() => ['success', 'empty'].includes(state.value?.status)),
        isEmpty: computed(() => state.value?.status === 'empty'),
        refresh: () => activationError.value ? activateEntry() : controller.retry()
    };

    const activateEntry = async () => {
        const version = ++activationVersion;
        const mapping = routeMapping.value;

        if (!sessionStore.isAuthenticated || !mapping.handled) {
            activationError.value = null;
            activationLoading.value = false;
            return null;
        }
        if (suppressNextFinderActivation && entry.value.type === 'finder') {
            suppressNextFinderActivation = false;
            return null;
        }

        activationError.value = null;
        activationLoading.value = true;
        try {
            let query = mapping.query;

            if (mapping.savedName) {
                const record = await repository.getSaved(mapping.savedName);
                query = record?.definition || createEmptyMarketQuery();
            } else if (mapping.restoreLast) {
                const record = await repository.getLast();
                query = record?.definition || createEmptyMarketQuery();
            }

            if (version !== activationVersion) return null;
            return controller.apply(query, { persist: false, keepInitialization: true });
        } catch (error) {
            if (version !== activationVersion || error.canceled) return null;
            activationError.value = error;
            throw error;
        } finally {
            if (version === activationVersion) activationLoading.value = false;
        }
    };

    watch(
        [() => entry.value.signature, () => sessionStore.uid, () => sessionStore.isAuthenticated],
        () => {
            activateEntry().catch(() => {});
        },
        { immediate: true }
    );

    const beginDraft = () => controller.cancelDraft();
    const cancelDraft = () => controller.cancelDraft();
    const clearDraft = () => controller.resetDraft();
    const setDraft = (query) => controller.setDraft(query);
    const toggleSelection = (selection) => {
        const query = cloneMarketQuery(state.value?.draftQuery || createEmptyMarketQuery());
        const index = query.selections.indexOf(selection);

        if (index === -1) query.selections.push(selection);
        else query.selections.splice(index, 1);
        controller.setDraft(query);
    };
    const setDescription = (description) => {
        const query = cloneMarketQuery(state.value?.draftQuery || createEmptyMarketQuery());
        query.description = String(description || '').slice(0, 200);
        controller.setDraft(query);
    };
    const applyDraft = async () => {
        const query = cloneMarketQuery(state.value?.draftQuery || createEmptyMarketQuery());

        if (entry.value.type !== 'finder') {
            suppressNextFinderActivation = true;
            await openSource({ type: 'finder' });
        }

        await invalidateChartingMarketQuery(sessionStore.uid, buildChartingMarketQueryCacheSignature(query));
        return controller.apply(query);
    };
    const applySort = async (value) => {
        const sort = chartingSortToMarketSort(value);
        if (!sort) return { skipped: true };
        const query = cloneMarketQuery(state.value?.appliedQuery || createEmptyMarketQuery());
        query.sort = sort;
        await invalidateChartingMarketQuery(sessionStore.uid, buildChartingMarketQueryCacheSignature(query));
        return controller.apply(query);
    };

    const pagination = computed(() => state.value?.meta?.pagination || null);
    const restrictedSelections = computed(() => state.value?.meta?.restricted_selections || {});
    const summary = computed(() => summarizeMarketQuery(state.value?.appliedQuery || createEmptyMarketQuery()));

    const patchFavoriteMembership = ({ ticker, direction, folderName, isFavorite }) => {
        const normalizedTicker = String(ticker || '').trim().toUpperCase();
        const normalizedFolderName = String(folderName || '').trim();

        if (!isActive.value || !normalizedTicker || !normalizedFolderName || !state.value?.items) {
            return false;
        }

        let changed = false;
        const nextItems = state.value.items.map((item) => {
            const itemTicker = String(item?.ticker || item?.symbol || '').trim().toUpperCase();
            const itemDirection = item?.d ?? item?.direccio ?? item?.direction;
            const directionMatches = direction === null || direction === undefined || direction === '' || String(itemDirection) === String(direction);

            if (itemTicker !== normalizedTicker || !directionMatches) {
                return item;
            }

            const currentFolders = Array.isArray(item.folder_name)
                ? item.folder_name
                : Array.isArray(item.folderNames)
                    ? item.folderNames
                    : [item.folder_name || item.folderNames].filter(Boolean);
            const nextFolders = isFavorite
                ? [...new Set([...currentFolders, normalizedFolderName])]
                : currentFolders.filter((name) => name !== normalizedFolderName);
            changed = true;

            return {
                ...item,
                folder_name: nextFolders,
                favorites: nextFolders.length > 0
            };
        });

        if (changed) {
            state.value = {
                ...state.value,
                items: nextItems
            };
        }

        return changed;
    };

    onBeforeUnmount(() => {
        activationVersion += 1;
        controller.destroy();
    });

    return {
        state,
        resource,
        isActive,
        selectionOptions: CHARTING_MARKET_SELECTIONS,
        summary,
        pagination,
        restrictedSelections,
        total: computed(() => normalizedCollection.value.totalItems),
        hasMore: computed(() => Boolean(pagination.value?.has_more)),
        beginDraft,
        cancelDraft,
        clearDraft,
        setDraft,
        toggleSelection,
        setDescription,
        applyDraft,
        applySort,
        patchFavoriteMembership,
        retry: () => activationError.value ? activateEntry() : controller.retry(),
        loadNextPage: () => controller.loadNextPage(),
        saveNamed: (name) => controller.saveNamed(name),
        activateEntry
    };
}
